import SntBrowserTabTitle from "@cleanup/components/SntBrowserTabTitle";
import BackHistory from "@/containers/BackHistory/BackHistory";
import { useLocation } from "react-router-dom";
import { isShowBackEntry } from "@/types/NavigationState";
import SensolusOnlyBadge from "@/cleanup/widgets/SensolusOnlyBadge";
import SntHideLongText from "@/components/ReactBootstrap/SntHideLongText";
import Cookies from "js-cookie";
import React from "react";

import styled from "styled-components";

const PageHeaderStyle = styled.h1`
  font-size: 24px;
  margin: 0;
  padding: 0;
  background: #fff;
  color: #212851;
  font-weight: 300;
  width: auto;
  box-shadow: 0 0px 0 #fff;
  border-bottom-color: transparent;
  line-height: 1.3;

  max-width: 500px;
  overflow: hidden;
  text-overflow: ellipsis;
  height: auto;
  white-space: nowrap;
`;

interface PageHeaderProps {
  title: React.ReactNode;
  extraTitle?: React.ReactNode;
  tabTitle?: string;
  renderRightHeader?: () => React.ReactNode;
  renderHeaderIcon?: () => React.ReactNode;
  /**
   * Slot rendered before the title — typically an entity image/icon
   * (device photo, geozone image). Enables the two-row layout with
   * subtitleRender below (see `AssetDetailPageHeader` legacy shape).
   */
  renderLeadingIcon?: () => React.ReactNode;
  /**
   * Second row below the title — chips, address, metadata. When set,
   * the header switches to the leading-icon layout regardless of
   * whether renderLeadingIcon is passed.
   */
  subtitleRender?: () => React.ReactNode;
  /**
   * Back arrow is opt-in: only detail/drill-in pages (entity detail,
   * edit, create, import flows) should enable it. List, dashboard and
   * settings pages must not show a back arrow (STIC-15588).
   */
  isShowBackHistory?: boolean;
  /**
   * Canonical parent route for the back arrow when the page is opened
   * without in-app history (direct URL / bookmark). See BackHistory.
   */
  backTo?: string;
  /** When true, renders a "Sensolus only" badge next to the page title. */
  systemOnly?: boolean;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  extraTitle,
  tabTitle,
  renderRightHeader,
  renderHeaderIcon,
  renderLeadingIcon,
  subtitleRender,
  isShowBackHistory = false,
  backTo,
  systemOnly,
}) => {
  const sensolusFullScreen = Cookies.get("sensolus-full-screen");
  const location = useLocation();
  // Show the back arrow when the page opts in statically, OR when it was
  // reached via a content click-through that requested it (dashboard widget
  // → its list view). BackHistory still hides it for genuine menu-root entries.
  const showBackArrow = isShowBackHistory || isShowBackEntry(location);

  if (sensolusFullScreen) return <></>;

  const useLeadingLayout = !!(renderLeadingIcon || subtitleRender);

  if (useLeadingLayout) {
    return (
      <>
        <SntBrowserTabTitle title={tabTitle || (typeof title === "string" ? title : undefined)} />
        {/* Three-column grid — [back arrow] [leading icon] [title + actions] —
            with the subtitle row placed in the title column, so it lines up
            under the title text whether or not the arrow or icon is rendered
            (an absent column collapses to 0 wide). A fixed indent assumed
            both were always present and drifted 40px on icon-less pages. */}
        <div
          className="heading"
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto minmax(0, 1fr)",
            alignItems: "center",
            paddingTop: 8,
            paddingBottom: 8,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <div style={{ gridColumn: 1 }}>{showBackArrow && <BackHistory backTo={backTo} />}</div>
          <div style={{ gridColumn: 2 }}>
            {renderLeadingIcon && <div className="me-2">{renderLeadingIcon()}</div>}
          </div>
          <div className="d-flex align-items-center" style={{ gridColumn: 3, minWidth: 0 }}>
            <h1
              className="page-header mb-0"
              style={{ marginTop: 0, minWidth: 0, overflow: "hidden" }}
            >
              <SntHideLongText>{title}</SntHideLongText>
              <SensolusOnlyBadge systemOnly={systemOnly} />
            </h1>
            {extraTitle && <> {extraTitle}</>}
            {/* Header actions — the view/edit toggle, favourite, impersonate —
                sit in the right-hand group with the page's own actions rather
                than trailing the title, so every page puts them in the same
                place (STIC-15985). */}
            <div className="d-flex justify-content-end align-items-center flex-shrink-0 ms-auto gap-2">
              {renderHeaderIcon?.()}
              {renderRightHeader?.()}
            </div>
          </div>
          {subtitleRender && (
            <div
              className="d-flex align-items-center flex-wrap"
              style={{ gridColumn: 3, marginTop: 8 }}
            >
              {subtitleRender()}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <SntBrowserTabTitle title={tabTitle || (typeof title === 'string' ? title : undefined)} />

      <div
        className="d-flex justify-content-between align-items-center heading"
        style={{ margin: "12.5px 0 12.5px 0" }}
      >
        <div>
          <div className="d-flex align-items-baseline" title={tabTitle}>
            {showBackArrow && <BackHistory backTo={backTo} />}
            <PageHeaderStyle>
              {title}
              <SensolusOnlyBadge systemOnly={systemOnly} />
            </PageHeaderStyle>
            {extraTitle && <> {extraTitle}</>}
          </div>
        </div>
        <div style={{ minWidth: 180, flex: 1 }}>
          <div className="d-flex justify-content-end align-items-center gap-2">
            {renderHeaderIcon && renderHeaderIcon()}
            {renderRightHeader && renderRightHeader()}
          </div>
        </div>
      </div>
    </>
  );
};

export default PageHeader;
